import { useEffect, useState } from "react"

/**
 * A new HQ, noticed by HQ itself (owners, 15 Oct 2026: the album ratings were live but a phone kept
 * showing the copy it had loaded before the update). A Home Screen app on an iPhone stays in memory
 * for days and never asks for the new version on its own.
 *
 * Each build names its main script by its contents (/assets/index-<hash>.js). When HQ comes back to
 * the screen, and every 15 minutes, it reads the page's current script name. A different one means a
 * new HQ: after 10+ minutes in the background it simply reloads (custom-order drafts are kept on the
 * device, lib/drafts.ts); otherwise a bar offers the reload. In local dev there is no such script and
 * nothing is checked.
 */
const SCRIPT = /\/assets\/index-[A-Za-z0-9_-]+\.js/
const CHECK_EVERY_MS = 15 * 60_000
const RELOAD_AFTER_HIDDEN_MS = 10 * 60_000

export const runningScript = () => document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.getAttribute("src")?.match(SCRIPT)?.[0] ?? null

/** The script the current HQ page names, or null when it cannot be read (offline). */
export async function latestScript(fetcher: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetcher("/", { cache: "no-store", credentials: "same-origin" })
    if (!res.ok) return null
    return (await res.text()).match(SCRIPT)?.[0] ?? null
  } catch {
    return null
  }
}

/** A newer HQ is live: true once it is seen. */
export function useAppUpdate(): boolean {
  const [available, setAvailable] = useState(false)
  useEffect(() => {
    const running = runningScript()
    if (!running) return
    let hiddenAt: number | null = null
    let stopped = false
    const check = async (comingBack: boolean) => {
      const latest = await latestScript()
      if (stopped || !latest || latest === running) return
      const away = hiddenAt != null ? Date.now() - hiddenAt : 0
      const typing = document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement
      if (comingBack && away >= RELOAD_AFTER_HIDDEN_MS && !typing) window.location.reload()
      else setAvailable(true)
    }
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now()
      else {
        void check(true)
        hiddenAt = null
      }
    }
    // A section's file from the old version can be gone after a deploy: load the new HQ instead.
    const onPreloadError = (event: Event) => {
      event.preventDefault()
      const message = String((event as Event & { payload?: unknown }).payload ?? "")
      const files = message.match(/\/assets\/[A-Za-z0-9_.-]+/g) ?? []
      void Promise.all(files.map((file) => fetch(file, { cache: "reload" }).catch(() => null))).then(() => {
        const repair = (window as Window & { __hqRepair?: (force: boolean) => void }).__hqRepair
        if (repair) repair(false)
        else window.location.reload()
      })
    }
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("vite:preloadError", onPreloadError)
    const timer = window.setInterval(() => void check(false), CHECK_EVERY_MS)
    void check(false)
    return () => {
      stopped = true
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("vite:preloadError", onPreloadError)
      window.clearInterval(timer)
    }
  }, [])
  return available
}
